import QuoteDrawer from '../../../../console/quotes/drawers/policyholder/PolicyHolderQuoteDrawer';
import { PolicyHolderQuoteDrawerHeader } from '../../../../console/quotes/drawers/policyholder/PolicyHolderQuoteDrawerHeader';
import PolicyHolderQuoteSummary from '../../../../console/quotes/drawers/policyholder/PolicyHolderQuoteSummary';
import { P100QuoteMidTermEndorsementDrawer } from '../../../../console/_global/endorsements/P100QuoteMidTermEndorsementDrawer';
import { ProductTypes } from '../../../../types';
import type { Source } from '../../types';
import { PolicyHolderCoverageBlocks } from './PolicyHolderCoverageBlocks';
import { resolveP100RaterVersionFromEntity } from '../../../../console/workflows/quotes/prime-100/utils';

interface Props {
  readonly open: boolean;
  readonly source: Source | null;
  readonly onClose: () => void;
}

/**
 * Ask Moo overlay for a policyholder quote citation. The customer persona has no
 * ``/customer/quote/detail`` route (see buildQuotePath), so the "View quote"
 * action opens this drawer instead of navigating. Mirrors PolicyOverlayDrawer:
 * QuoteDrawer self-fetches from quoteId + product and feeds the hydrated quote
 * to the same header / summary / coverage pieces the policyholder listing uses.
 */
export function QuoteOverlayDrawer({ open, source, onClose }: Props) {
  if (!source) return null;

  const meta = (source.metadata ?? {}) as Record<string, unknown>;
  const quoteId = (meta.objectId || meta.quoteNumber) as string;
  const product = meta.productType as string;

  return (
    <QuoteDrawer
      quoteId={quoteId}
      product={product}
      open={open}
      onClose={onClose}
    >
      {({ quote = {} }: { quote?: any }) => {
        const merged = { ...meta, ...quote };
        // Midterm P100 endorsement quotes get a dedicated body (mirrors the
        // policyholder quotes listing, which hides the standard view here).
        if (merged.isEndorsement && product === ProductTypes.p100) {
          return (
            <div
              style={{ marginTop: 8, overflowY: 'auto', overflowX: 'hidden' }}
            >
              <P100QuoteMidTermEndorsementDrawer
                quote={{ ...quote, ...quote.coverages }}
              />
            </div>
          );
        }
        return (
          <>
            <PolicyHolderQuoteDrawerHeader quote={merged} />
            <div style={{ marginTop: 8, overflowY: 'auto', overflowX: 'hidden' }}>
              <PolicyHolderQuoteSummary quote={merged} />
              <PolicyHolderCoverageBlocks
                product={product}
                coverages={quote.coverages}
                boostVersion={resolveP100RaterVersionFromEntity(quote)}
              />
            </div>
          </>
        );
      }}
    </QuoteDrawer>
  );
}
